import { PlusIcon, StackIcon } from "@xola/icons";
import { Alert, Button, flash, SubmitButton, Switch } from "@xola/ui-kit";
import { countBy, uniq } from "lodash";
import React, { useMemo } from "react";
import { useMutation } from "react-query";
import { Experience, ExperienceOption } from "types";
import { EditExperienceOptionModal } from "./EditExperienceOptionModal";
import { ExperienceOptionItem } from "./ExperienceOptionItem";
import { api } from "@/api/api";
import { HeaderToolBarActions } from "@/components/Common/HeaderToolbarActions";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { XolaFormik } from "@/components/Common/XolaFormik";
import { ContentLayout } from "@/components/Layout/ContentLayout";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useModal } from "@/hooks/useVisibility";
import { getActiveOptions, isOptionsEnabled } from "@/models/Experience";

interface ExperienceOptionListProps {
    experience: Experience;
}

interface FormValues {
    enabled: boolean;
}

export const ExperienceOptionList = ({ experience }: ExperienceOptionListProps) => {
    const createModal = useModal(false);
    const invalidate = useInvalidate();
    const activeOptions = getActiveOptions(experience);

    const { mutateAsync: save, isLoading: isSaving } = useMutation(async (enabled: boolean) => {
        await api.put(`/experiences/${experience.id}`, { optionsPreference: { enabled } });
    });

    const handleSubmit = async (values: FormValues) => {
        await save(values.enabled);
        invalidate("experiences", experience.id);
        flash.success("Experience Option settings saved");
    };

    return (
        <ContentLayout isWide isDisabled={isSaving}>
            <HeaderToolBarActions>
                <Button
                    color="primary"
                    icon={<PlusIcon />}
                    data-testid="experience-option-create-button"
                    onClick={createModal.show}
                >
                    Create Experience Option
                </Button>
            </HeaderToolBarActions>
            <XolaFormik<FormValues>
                enableReinitialize
                initialValues={{ enabled: isOptionsEnabled(experience) }}
                onSubmit={handleSubmit}
            >
                {({ values, setFieldValue, handleSubmit, handleReset, dirty }) => (
                    <>
                        <div className="mt-5 space-y-4 px-1.5">
                            <div className="text-xl font-bold">Experience Options</div>

                            <Switch.Group>
                                <Switch
                                    size="medium"
                                    data-testid="experience-options-enable-toggle"
                                    isChecked={values.enabled}
                                    onChange={(enabled: boolean) => setFieldValue("enabled", enabled)}
                                />
                                <Switch.Label className="ml-2">
                                    Enable experience options for this experience
                                </Switch.Label>
                            </Switch.Group>

                            {values.enabled && (
                                <ExperienceOptionsSection
                                    experience={experience}
                                    options={activeOptions}
                                    onCreate={createModal.show}
                                />
                            )}
                        </div>

                        <ContentLayout.Footer preferenceSidebar>
                            <SubmitButton
                                size="medium"
                                data-testid="experience-options-save-button"
                                isLoading={isSaving}
                                disabled={!dirty}
                                onClick={handleSubmit}
                            >
                                Save
                            </SubmitButton>
                            <SecondaryButton
                                data-testid="experience-options-cancel-button"
                                disabled={!dirty}
                                onClick={handleReset}
                            >
                                Cancel
                            </SecondaryButton>
                        </ContentLayout.Footer>
                    </>
                )}
            </XolaFormik>
            <EditExperienceOptionModal
                experience={experience}
                isOpen={createModal.isVisible}
                onClose={createModal.hide}
            />
        </ContentLayout>
    );
};

interface ExperienceOptionsSectionProps {
    experience: Experience;
    options: ExperienceOption[];
    onCreate: () => void;
}

const ExperienceOptionsSection = ({ experience, options, onCreate }: ExperienceOptionsSectionProps) => {
    const scheduleCountByOptionId = useMemo(
        () =>
            countBy(
                (experience.schedules ?? []).flatMap((schedule) =>
                    uniq((schedule.options ?? []).map((scheduleOption) => scheduleOption.optionId)),
                ),
            ),
        [experience.schedules],
    );

    if (options.length === 0) {
        return <NoExperienceOptionsAlert onCreate={onCreate} />;
    }

    return (
        <div className="divide-y divide-gray-light rounded pr-1">
            {options.map((option) => (
                <ExperienceOptionItem
                    key={option.id}
                    experience={experience}
                    option={option}
                    scheduleCount={scheduleCountByOptionId[option.id] ?? 0}
                />
            ))}
        </div>
    );
};

const NoExperienceOptionsAlert = ({ onCreate }: { onCreate: () => void }) => (
    <Alert color="primary">
        <div className="flex p-2">
            <StackIcon size="large" />
            <div className="ml-2 flex-1">
                <span className="mb-3 block text-md font-bold" data-testid="experience-option-list-no-options-found">
                    No Experience Options Found
                </span>
                <span className="text-base">
                    You do not have any Experience Options yet. Create one to attach it to a schedule.
                </span>

                <div className="mt-2 flex justify-end">
                    <Button
                        color="primary"
                        size="small"
                        icon={<PlusIcon />}
                        data-testid="experience-option-empty-create-button"
                        onClick={onCreate}
                    >
                        Create Experience Option
                    </Button>
                </div>
            </div>
        </div>
    </Alert>
);
